import {
  getBrandKeyFromEnv,
  isVisibleForBrand,
  type BrandVisibilityRule,
} from "@/lib/branding";

/** Messengers offered next to the support phone of a registration form. */
export type SupportChannelKey = "whatsapp" | "telegram" | "max";

export type SupportChannelConfig = BrandVisibilityRule & {
  key: SupportChannelKey;
  /** Brand name, identical in every locale — deliberately not an i18n key. */
  name: string;
  /**
   * What the chat link is built from.
   * "phone" reuses the support phone, "username" asks for its own handle
   * in a field that appears once the channel is switched on.
   */
  source: "phone" | "username";
  /** Подсказки поля различаются: Telegram принимает и номер, MAX — нет. */
  placeholderKey?: string;
  hintKey?: string;
};

export const SUPPORT_CHANNEL_CONFIGS: readonly SupportChannelConfig[] = [
  // WhatsApp is on its way out in Russia, so examru offers Telegram and MAX instead.
  {
    key: "whatsapp",
    name: "WhatsApp",
    source: "phone",
    hideForBrands: ["examru"],
  },
  {
    key: "telegram",
    name: "Telegram",
    source: "username",
    placeholderKey: "ui-telegram-username-placeholder",
    hintKey: "ui-telegram-username-hint",
  },
  {
    key: "max",
    name: "MAX",
    source: "username",
    placeholderKey: "ui-max-username-placeholder",
    hintKey: "ui-max-username-hint",
  },
];

export type SupportChannelFlags = Record<SupportChannelKey, boolean>;

/** Handles of the channels addressed by username rather than by phone. */
export type SupportChannelUsernames = { telegram: string; max: string };

export const DEFAULT_SUPPORT_CHANNELS: SupportChannelFlags = {
  whatsapp: false,
  telegram: false,
  max: false,
};

export const DEFAULT_SUPPORT_USERNAMES: SupportChannelUsernames = {
  telegram: "",
  max: "",
};

export function getVisibleSupportChannels(
  brandKey: string = getBrandKeyFromEnv(),
): SupportChannelConfig[] {
  return SUPPORT_CHANNEL_CONFIGS.filter((channel) =>
    isVisibleForBrand(channel, brandKey),
  );
}

/**
 * Forces channels hidden for the current brand to be disabled.
 * Keeps the editor and the public form in sync when a stored form still enables
 * a messenger that the brand no longer offers.
 */
export function disableBrandHiddenChannels<
  T extends Partial<SupportChannelFlags>,
>(channels: T, brandKey: string = getBrandKeyFromEnv()): T {
  return SUPPORT_CHANNEL_CONFIGS.reduce(
    (acc, channel) =>
      isVisibleForBrand(channel, brandKey)
        ? acc
        : { ...acc, [channel.key]: false },
    channels,
  );
}

/**
 * Accepts "@name", "name" or a full profile link and returns the bare handle.
 *
 * Ведущих «@» снимаем сколько угодно: подпись контакта потом добавляет ровно
 * один, а пользователь легко вводит «@@name», дописав собственный к тому, что
 * уже был скопирован.
 */
export function normalizeUsername(raw: string | null | undefined): string {
  const value = (raw ?? "").trim();
  if (!value) return "";

  const withoutLink = value
    .replace(/^https?:\/\//i, "")
    .replace(/^(t\.me|telegram\.me|max\.ru|max\.me)\//i, "")
    .replace(/^@+/, "");

  const withoutQuery = withoutLink.split(/[?#]/)[0]?.trim() ?? "";

  // Кнопка «Поделиться» в MAX выдаёт ссылку вида max.ru/u/<хеш> — это тоже
  // адрес профиля, и обрезать его до «u» нельзя.
  const byHash = withoutQuery.match(/^u\/([^/]+)/i);
  if (byHash) return `u/${byHash[1]}`;

  return withoutQuery.split("/")[0]?.trim() ?? "";
}

/**
 * True when the value is a phone number rather than a handle.
 *
 * Telegram accepts both, but MAX addresses a profile only by username — a
 * manager used to WhatsApp may type a number there, and the link would come out
 * syntactically valid (max.ru/79001234567) yet lead nowhere.
 */
export function isPhoneLike(raw: string | null | undefined): boolean {
  const value = normalizeUsername(raw).replace(/^\+/, "");
  if (!value) return false;

  const digits = value.replace(/\D/g, "");
  return digits.length >= 8 && digits.length === value.length;
}

export type SupportChannelSources = {
  phone?: string | null;
  usernames?: Partial<SupportChannelUsernames> | null;
};

/**
 * Builds the chat link for a channel, or null when there is nothing to link to.
 *
 * WhatsApp wants digits only. Telegram resolves both `t.me/<username>` and
 * `t.me/+<phone>`, so a number typed by mistake still works. MAX has no
 * documented phone link, so a number there is rejected outright.
 */
export function buildSupportChannelUrl(
  key: SupportChannelKey,
  sources: SupportChannelSources,
): string | null {
  if (key === "whatsapp") {
    const digits = (sources.phone ?? "").replace(/\D/g, "");
    return digits.length >= 8 ? `https://wa.me/${digits}` : null;
  }

  const handle = normalizeUsername(sources.usernames?.[key]);
  if (!handle) return null;

  if (key === "telegram") {
    return isPhoneLike(handle)
      ? `https://t.me/+${handle.replace(/\D/g, "")}`
      : `https://t.me/${handle}`;
  }

  // MAX
  return isPhoneLike(handle) ? null : `https://max.ru/${handle}`;
}

/**
 * What to print under the channel name.
 *
 * Ники показываем всегда с «@», как их принято писать, независимо от того,
 * ввёл его менеджер или нет. Номер телефона остаётся номером.
 */
export function getSupportChannelHandle(
  key: SupportChannelKey,
  sources: SupportChannelSources,
): string {
  if (key === "whatsapp") return (sources.phone ?? "").trim();

  const handle = normalizeUsername(sources.usernames?.[key]);
  if (!handle) return "";

  // «@» уместен только у ника. Номер остаётся номером, а ссылки вида u/<хеш> и
  // приглашение в закрытый чат +<хеш> показываем адресом, а не ником.
  if (isPhoneLike(handle)) return handle;
  if (handle.includes("/")) return `max.ru/${handle}`;
  if (/^\+(?=[\w-]*[A-Za-z_])[\w-]+$/.test(handle)) return `t.me/${handle}`;

  return `@${handle}`;
}

/**
 * True when the channel can actually be switched on.
 *
 * Username-based channels are switched on first and filled in afterwards — the
 * field only appears once the toggle is active — so they are always allowed.
 * WhatsApp still needs the phone up front, since it has no field of its own.
 */
export function canEnableSupportChannel(
  channel: SupportChannelConfig,
  sources: SupportChannelSources,
): boolean {
  if (channel.source === "username") return true;
  return buildSupportChannelUrl(channel.key, sources) !== null;
}
